import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { AuthResponse } from '@comms/shared';
import { api } from '../lib/api';
import { isAndroidApp } from '../lib/native';
import { startSession } from '../lib/session';
import { Button, ErrorText, Input } from '../components/ui';

export function AuthView({ mode }: { mode: 'login' | 'register' }) {
  const [params] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteCode, setInviteCode] = useState(params.get('invite') ?? '');
  const [totp, setTotp] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'register') {
        const res = await api<AuthResponse>('POST', '/auth/register', {
          username: username.trim().toLowerCase(),
          password,
          inviteCode: inviteCode.trim(),
          ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
        });
        await startSession(res.user, res.csrfToken);
      } else {
        const res = await api<AuthResponse | { mfaRequired: true }>('POST', '/auth/login', {
          username: username.trim(),
          password,
          ...(needsMfa ? { totp } : {}),
        });
        if ('mfaRequired' in res) {
          setNeedsMfa(true);
          return;
        }
        await startSession(res.user, res.csrfToken);
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-[#404eed] to-[#23272a] p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md space-y-4 rounded-lg bg-bg-2 p-8 shadow-2xl"
      >
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-0">
            {mode === 'login' ? 'Welcome back!' : 'Create an account'}
          </h1>
          <p className="mt-1 text-sm text-text-2">
            {mode === 'login'
              ? 'Sign in to your private server.'
              : 'You need an invite from someone on this server.'}
          </p>
        </div>
        {needsMfa ? (
          <Input
            label="Two-factor code"
            autoComplete="one-time-code"
            inputMode="numeric"
            value={totp}
            onChange={(e) => setTotp(e.target.value)}
            placeholder="6-digit code or recovery code"
            required
            autoFocus
          />
        ) : (
          <>
            {mode === 'register' && (
              <Input
                label="Invite code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                required
              />
            )}
            <Input
              label="Username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
            {mode === 'register' && (
              <Input
                label="Display name (optional)"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            )}
            <Input
              label="Password"
              type="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={mode === 'register' ? 10 : undefined}
              required
            />
            {mode === 'register' && (
              <p className="text-xs text-text-3">At least 10 characters. A passphrase is best.</p>
            )}
          </>
        )}
        <ErrorText error={error} />
        <Button type="submit" disabled={busy} className="w-full">
          {mode === 'login' ? (needsMfa ? 'Verify' : 'Log in') : 'Create account'}
        </Button>
        <p className="text-sm text-text-3">
          {mode === 'login' ? (
            <>
              Have an invite?{' '}
              <Link to={`/register${location.search}`} className="text-accent hover:underline">
                Register
              </Link>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <Link to={`/login${location.search}`} className="text-accent hover:underline">
                Log in
              </Link>
            </>
          )}
        </p>
        {/Android/.test(navigator.userAgent) && !isAndroidApp() && (
          <p className="text-sm text-text-3">
            On Android?{' '}
            <Link to="/android" className="text-accent hover:underline">
              Get the app
            </Link>
          </p>
        )}
      </form>
    </div>
  );
}
